/**
 * /erp/settings/transports — manage transport units (Manager+).
 *
 * Single-page CRUD: top create form + list with inline edit /
 * activate-toggle / delete. Mirrors the warehouses + drivers pages.
 *
 * Per Q10 the only required field is ``vehicle_number``; ``type`` is
 * free text (no enum) because v1 doesn't try to taxonomise fleet
 * categories — a single business might mix "truck", "trailer", "tempo",
 * and there's no universal vocabulary worth enforcing.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { AlertCircle, Loader2, Pencil, Plus, Save, Trash2, Truck, X } from "lucide-react";
import { type FormEvent, useState } from "react";

import {
  ApiError,
  type ErpTransport,
  type ErpTransportCreate,
  type ErpTransportUpdate,
  erpApi,
  isErpDispatchMasterConflict,
} from "../lib/api";
import { ERP_QUERY_KEYS, useTransports } from "../lib/erp";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/erp/settings/transports")({
  beforeLoad: () => {
    throw redirect({ to: "/settings/transports" });
  },
  component: TransportsAdminPage,
});

export function TransportsAdminPage() {
  const queryClient = useQueryClient();
  const transportsQ = useTransports();
  const transports = transportsQ.data?.items ?? [];

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.transports });
  };

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <Truck className="size-5 opacity-70" aria-hidden />
          Transport
        </h2>
        <p className="text-sm opacity-60">
          Vehicles you'll attach to a Dispatch. Codes auto-generate as TRP-NNNNN — override on
          create if you'd prefer something descriptive (e.g.
          <code className="mx-1">FLEET-01</code>). The "type" field is free text — use whatever
          vocabulary matches your operation.
        </p>
      </header>

      <CreateTransportBar onCreated={onChanged} />

      {transportsQ.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : transportsQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load transports: {(transportsQ.error as Error).message}
        </p>
      ) : transports.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
          <Truck className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
          <p className="text-sm font-medium">No transports yet.</p>
          <p className="mt-1 text-sm opacity-60">Add one above to get started.</p>
        </div>
      ) : (
        <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
          {transports.map((t) => (
            <TransportRow key={t.id} transport={t} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateTransportBar({ onCreated }: { onCreated: () => void }) {
  const [code, setCode] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [type, setType] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (body: ErpTransportCreate) => erpApi.transports.create(body),
    onSuccess: () => {
      setCode("");
      setVehicleNumber("");
      setType("");
      setError(null);
      onCreated();
      toast.success("Transport created.");
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        isErpDispatchMasterConflict(err.detail)
      ) {
        setError(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not create transport.";
      setError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!vehicleNumber.trim()) {
      setError("Vehicle number is required.");
      return;
    }
    setError(null);
    const body: ErpTransportCreate = { vehicle_number: vehicleNumber.trim() };
    if (code.trim()) body.code = code.trim();
    if (type.trim()) body.type = type.trim();
    createMutation.mutate(body);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-xl border border-[var(--border)] p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Code</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={32}
            placeholder="auto"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Vehicle number <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={vehicleNumber}
            onChange={(e) => setVehicleNumber(e.target.value)}
            maxLength={50}
            required
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Type</span>
          <input
            type="text"
            value={type}
            onChange={(e) => setType(e.target.value)}
            maxLength={50}
            placeholder="e.g. truck, van, lorry"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
      </div>

      {error && (
        <p className="flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
          <AlertCircle className="size-3" aria-hidden />
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={createMutation.isPending || !vehicleNumber.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {createMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Plus className="size-4" aria-hidden />
          )}
          Add transport
        </button>
      </div>
    </form>
  );
}

function TransportRow({
  transport,
  onChanged,
}: {
  transport: ErpTransport;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [vehicleNumber, setVehicleNumber] = useState(transport.vehicle_number);
  const [type, setType] = useState(transport.type ?? "");

  const updateMutation = useMutation({
    mutationFn: (body: ErpTransportUpdate) => erpApi.transports.update(transport.id, body),
    onSuccess: () => {
      onChanged();
      setEditing(false);
      toast.success("Saved.");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not save.";
      toast.error(msg);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => erpApi.transports.remove(transport.id),
    onSuccess: () => {
      onChanged();
      toast.success("Transport deleted.");
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        isErpDispatchMasterConflict(err.detail)
      ) {
        toast.error(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not delete.";
      toast.error(msg);
    },
  });

  const startEdit = () => {
    setVehicleNumber(transport.vehicle_number);
    setType(transport.type ?? "");
    setEditing(true);
  };

  const submitEdit = () => {
    const body: ErpTransportUpdate = {};
    if (vehicleNumber.trim() !== transport.vehicle_number) {
      body.vehicle_number = vehicleNumber.trim();
    }
    const newType = type.trim() || null;
    if (newType !== (transport.type ?? null)) body.type = newType;
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    updateMutation.mutate(body);
  };

  const toggleActive = () => {
    updateMutation.mutate({ is_active: !transport.is_active });
  };

  const handleDelete = () => {
    if (window.confirm(`Delete transport ${transport.code}? This cannot be undone.`)) {
      removeMutation.mutate();
    }
  };

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {editing ? (
          <div className="flex flex-wrap items-start gap-2">
            <span className="rounded-md bg-black/5 px-2 py-1 font-mono text-xs dark:bg-white/10">
              {transport.code}
            </span>
            <input
              type="text"
              value={vehicleNumber}
              onChange={(e) => setVehicleNumber(e.target.value)}
              maxLength={50}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 font-mono text-sm outline-none focus:border-[var(--accent)]"
              placeholder="Vehicle number"
            />
            <input
              type="text"
              value={type}
              onChange={(e) => setType(e.target.value)}
              maxLength={50}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
              placeholder="Type"
            />
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-black/5 px-2 py-0.5 font-mono text-xs dark:bg-white/10">
              {transport.code}
            </span>
            <span className="font-mono font-medium">{transport.vehicle_number}</span>
            {transport.type && (
              <span className="rounded-full border border-[var(--border)] px-2 py-0.5 text-[10px] uppercase tracking-wider opacity-70">
                {transport.type}
              </span>
            )}
            {!transport.is_active && (
              <span className="rounded-full bg-black/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider opacity-60 dark:bg-white/10">
                Inactive
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {editing ? (
          <>
            <button
              type="button"
              onClick={submitEdit}
              disabled={updateMutation.isPending}
              aria-label="Save"
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
            >
              {updateMutation.isPending ? (
                <Loader2 className="size-3 animate-spin" aria-hidden />
              ) : (
                <Save className="size-3" aria-hidden />
              )}
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              aria-label="Cancel"
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
            >
              <X className="size-3" aria-hidden />
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={toggleActive}
              disabled={updateMutation.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
            >
              {transport.is_active ? "Deactivate" : "Activate"}
            </button>
            <button
              type="button"
              onClick={startEdit}
              aria-label={`Edit ${transport.code}`}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
            >
              <Pencil className="size-3" aria-hidden />
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={removeMutation.isPending}
              aria-label={`Delete ${transport.code}`}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40"
            >
              {removeMutation.isPending ? (
                <Loader2 className="size-3 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-3" aria-hidden />
              )}
              Delete
            </button>
          </>
        )}
      </div>
    </li>
  );
}
